/**
 * Shared decoders: they translate one API's encoding into the other's vocabulary so a field pair
 * can be compared at all.
 *
 * Anything used by more than one operation belongs here rather than in `operations/`. The process
 * status codes below are shared by every operation that returns processes - my-history,
 * process-overview, document-overview - which all speak the same status vocabulary.
 */
import {WORKFLOW_PROCESS_STATUS} from "utils/constants";

/*
 * The Web API returns one integer `status`; the Task API splits the same information into
 * `processStatus` (is it still running) and `processResolutionStatus` (how it ended). So 60 is both
 * "COMPLETED" and "CANCELLED", and neither Task API field alone can be compared against it.
 *
 * The codes come from the app's own WORKFLOW_PROCESS_STATUS so they cannot drift; what is declared
 * here is only how each code splits across the two Task API fields.
 */
const NAME_BY_CODE = Object.fromEntries(
    Object.entries(WORKFLOW_PROCESS_STATUS)
        .filter(([, value]) => typeof value === "number")
        .map(([name, value]) => [value, name]),
);

/**
 * ACK (40), ARCHIVED (50) and SUSPENDED (70) are deliberately absent: the code's name is known but
 * how it lands on the Task API's two fields is not - ARCHIVED, for one, says nothing about whether
 * the outcome was approval or rejection - and a guess here would show up as agreement. An unmapped
 * code is reported with its number so it is obvious what is missing.
 *
 * Confirmed against staging: 10, 20, 30 and 60 all agree on real data. 40, 50 and 70 do not occur
 * in the histories checked so far.
 */
const SPLIT_BY_NAME = {
    ACTIVE: {processStatus: "ACTIVE", processResolutionStatus: "AWAITING_APPROVAL"},
    APPROVED: {processStatus: "COMPLETED", processResolutionStatus: "APPROVED"},
    REJECTED: {processStatus: "COMPLETED", processResolutionStatus: "REJECTED"},
    CANCELLED: {processStatus: "COMPLETED", processResolutionStatus: "CANCELLED"},
};

/**
 * @param {number|null|undefined} code the Web API's integer status
 * @param {"processStatus"|"processResolutionStatus"} field which half to read
 * @returns {string|undefined} the Task API's value, or a marker naming the code when unknown
 */
export function decodeWebStatus(code, field) {
    if (code == null) {
        return undefined;
    }
    const name = NAME_BY_CODE[code];
    if (name == null) {
        return `status ${code}: not in WORKFLOW_PROCESS_STATUS`;
    }
    return SPLIT_BY_NAME[name]?.[field] ?? `${name} (${code}): split across the two fields is not known`;
}

/**
 * Ready-made `derived` entries for any operation whose two sides encode process status this way.
 * `webRoot` marks `status` as consumed, so it is not also reported as an unmapped Web API field.
 *
 * Spread them into an operation's `compare.derived`.
 */
export const PROCESS_STATUS_PAIRS = [
    {
        task: "processStatus",
        web: "status",
        webRoot: "status",
        note: "decoded from the Web API's integer status via WORKFLOW_PROCESS_STATUS",
        getWeb: item => decodeWebStatus(item.status, "processStatus"),
    },
    {
        task: "processResolutionStatus",
        web: "status",
        webRoot: "status",
        note: "decoded from the same integer: 60 is COMPLETED and CANCELLED at once",
        getWeb: item => decodeWebStatus(item.status, "processResolutionStatus"),
    },
];
